import React, { forwardRef, useImperativeHandle } from 'react';
import {
  IconButton,
  Tooltip,
  Box
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useTripPdfExport from '../hooks/useTripPdfExport';
import PdfExportProgressDialog from './PdfExportProgressDialog';

const PDFExportButton = forwardRef(({
  tripId,
  tripTitle,
  disabled = false,
  usePartnerAuth = false,
  onError,
}, ref) => {
  const { t } = useTranslation();
  const {
    downloadTripPdf,
    pdfProgressOpen,
    pdfProgress,
    pdfProgressPhase,
  } = useTripPdfExport();

  const downloadPDF = async () => {
    try {
      await downloadTripPdf({
        tripId,
        tripTitle,
        usePartnerAuth,
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      if (onError) {
        onError(errorMessage);
      } else {
        alert(`Failed to download PDF: ${errorMessage}`);
      }
      throw error;
    }
  };

  useImperativeHandle(ref, () => ({
    handleClick: downloadPDF,
    loading: () => pdfProgressOpen,
  }));

  return (
    <>
      <Tooltip title={t('export.downloadPdf')}>
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={downloadPDF}
            disabled={disabled || pdfProgressOpen}
            sx={{
              color: 'white',
              width: { xs: 56, sm: 56 },
              height: { xs: 56, sm: 56 },
              border: '2px solid rgba(255, 255, 255, 0.3)',
              backdropFilter: 'blur(15px)',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.3)',
                transform: 'scale(1.1)',
                borderColor: 'rgba(255, 255, 255, 0.5)'
              },
              '&:disabled': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
                color: 'rgba(255, 255, 255, 0.4)',
                opacity: 0.6
              },
              transition: 'all 0.3s ease'
            }}
          >
            <PdfIcon sx={{ fontSize: { xs: 24, sm: 24 } }} />
          </IconButton>
        </Box>
      </Tooltip>
      <PdfExportProgressDialog
        open={pdfProgressOpen}
        progress={pdfProgress}
        phase={pdfProgressPhase}
      />
    </>
  );
});

export default PDFExportButton;
